"use client"

import React, { useState, useEffect } from "react"
import DashboardLayout from "../components/layout/DashboardLayout"
import Button from "../components/common/Button"
import { User, Mail, Save, X, MapPin, Users, Building, Shield } from "lucide-react"
import { updateUserProfile, getUserProfile, UserProfile } from "../api/profileService"

// Custom SVG components for gender avatar icons
const FemaleIcon = ({ size, className }: { size: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path d="M12 10a4 4 0 100-8 4 4 0 000 8zM12 13c-4.42 0-8 3.58-8 8h16c0-4.42-3.58-8-8-8z" fill="white" />
  </svg>
)

const MaleIcon = ({ size, className }: { size: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path d="M12 10a4 4 0 100-8 4 4 0 000 8zM12 13c-4.42 0-8 3.58-8 8h16c0-4.42-3.58-8-8-8z" fill="white" />
  </svg>
)

const Profile: React.FC = () => {
  const [, setIsEditing] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userData, setUserData] = useState<UserProfile>({
    _id: "",
    name: "",
    email: "",
    phone: "",
    usertype: "",
    gender: "",
    country: "",
    state: "",
    city: "",
    companyId: "",
    companyName: "",
    isActive: false,
    isDeleted: false,
    createdAt: ""
  })
  const [editData, setEditData] = useState<UserProfile>({ ...userData })

  useEffect(() => {
    // Load user data from API
    const loadUserData = async () => {
      try {
        setError(null)
        const userStr = localStorage.getItem('users') || localStorage.getItem('user')
        if (!userStr) {
          setError('No user data found in localStorage')
          setIsLoading(false)
          return
        }

        const user = JSON.parse(userStr)
        console.log('User from localStorage:', user)
        
        // Check if user is admin
        if (!user.isAdmin) {
          // Redirect non-admin users to dashboard
          window.location.href = '/enhanced-admin-dashboard'
          return
        }

        // First set localStorage data immediately
        setUserData({
          _id: user._id || "",
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          usertype: user.usertype || "",
          gender: user.gender || "",
          country: user.country || "",
          state: user.state || "",
          city: user.city || "",
          companyId: user.companyId || "",
          companyName: user.companyName || "",
          isActive: user.isActive !== undefined ? user.isActive : true,
          isDeleted: user.isDeleted !== undefined ? user.isDeleted : false,
          createdAt: user.createdAt || ""
        })
        setEditData({
          _id: user._id || "",
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          usertype: user.usertype || "",
          gender: user.gender || "",
          country: user.country || "",
          state: user.state || "",
          city: user.city || "",
          companyId: user.companyId || "",
          companyName: user.companyName || "",
          isActive: user.isActive !== undefined ? user.isActive : true,
          isDeleted: user.isDeleted !== undefined ? user.isDeleted : false,
          createdAt: user.createdAt || ""
        })

        // Then try to fetch fresh data from API
        if (user._id) {
          try {
            console.log('Fetching user data from API for ID:', user._id)
            const apiUserData = await getUserProfile(user._id)
            console.log('API response:', apiUserData)
            
            setUserData(apiUserData)
            setEditData(apiUserData)
            
            // Update localStorage with fresh data
            const storageKey = localStorage.getItem('users') ? 'users' : 'user'
            localStorage.setItem(storageKey, JSON.stringify({
              ...user,
              ...apiUserData
            }))
          } catch (apiError) {
            console.warn('Failed to fetch from API, using localStorage data:', apiError)
            // Keep using localStorage data, no error shown to user
          }
        }
      } catch (error) {
        console.error("Error loading user data:", error)
        setError('Failed to load user data')
      } finally {
        setIsLoading(false)
      }
    }

    loadUserData()
  }, [])

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      
      console.log('Saving user data:', editData)
      
      // Call API to update user profile
      await updateUserProfile(userData._id, {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        gender: editData.gender,
        country: editData.country,
        state: editData.state,
        city: editData.city
      })
      
      // Update localStorage
      const currentUser = JSON.parse(localStorage.getItem('users') || localStorage.getItem('user') || '{}')
      const storageKey = localStorage.getItem('users') ? 'users' : 'user'
      localStorage.setItem(storageKey, JSON.stringify({
        ...currentUser,
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        gender: editData.gender,
        country: editData.country,
        state: editData.state,
        city: editData.city
      }))
      
      setUserData({ ...editData })
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating profile:', error)
      setError('Failed to update profile. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditData({ ...userData })
    setIsEditing(false)
    setError(null)
  }

  // const formatDate = (dateString: string) => {
  //   if (!dateString) return 'Not available'
  //   return new Date(dateString).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   })
  // }

  if (isLoading) {
    return (
      <DashboardLayout title="Profile">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (error) {
    return (
      <DashboardLayout title="Profile">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title="Profile">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-2xl font-bold border-3 border-white/30 shadow-lg">
                {userData.gender === 'female' ? (
                  <FemaleIcon size={32} className="text-pink-300" />
                ) : userData.gender === 'male' ? (
                  <MaleIcon size={32} className="text-blue-300" />
                ) : (
                  <User size={32} className="text-purple-300" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{userData.name || 'User Name'}</h1>
                <div className="flex items-center space-x-3 text-white/80 text-sm">
                  <span className="flex items-center">
                    <Shield size={14} className="mr-1" />
                    {userData.usertype?.replace('_', ' ').toUpperCase() || 'USER'}
                  </span>
                  <span className="flex items-center">
                    <Building size={14} className="mr-1" />
                    {userData.companyName || 'No Company'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Profile Information Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <User size={20} className="mr-2 text-purple-600" />
                Personal Information
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-text"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <div className="flex items-center">
                  <Mail size={16} className="text-gray-400 mr-2" />
                  <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg flex-1">{userData.email || 'Not specified'}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">Email cannot be edited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-text"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <select
                  value={editData.gender}
                  onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-pointer"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location & Company Information */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPin size={20} className="mr-2 text-blue-600" />
                Location & Company
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                <div className="flex items-center">
                  <Building size={16} className="text-gray-400 mr-2" />
                  <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg flex-1">{userData.companyName || 'Not specified'}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">Company cannot be edited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                <div className="flex items-center">
                  <Users size={16} className="text-gray-400 mr-2" />
                  <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg flex-1">{userData.usertype?.replace('_', ' ').toUpperCase() || 'USER'}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">User type cannot be edited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  value={editData.country}
                  onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-text"
                  placeholder="Enter your country"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  value={editData.state}
                  onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-text"
                  placeholder="Enter your state"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  value={editData.city}
                  onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent hover:border-purple-400 transition-colors cursor-text"
                  placeholder="Enter your city"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end">
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              className="bg-green-500 text-white hover:bg-green-600 font-semibold"
              disabled={isSaving}
            >
              <Save size={16} className="mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={handleCancel}
              className="bg-gray-500 text-white hover:bg-gray-600 font-semibold"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default Profile
